import { isVerifyRoute, Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { isSafeRedirectUrl } from '~/utils/auth/isSafeRedirectUrl';
import {
  AUTH_RESUME_REDIRECT_KEY,
  getSharedAuthStorageItem,
  removeSharedAuthStorageItem,
  setSharedAuthStorageItem,
} from '~/utils/auth/SessionHelpers';

const isAuthResumeGatePath = (redirect: string): boolean => {
  const pathname = new URL(redirect, window.location.origin).pathname;

  return pathname === Routes.verifyUser || isVerifyRoute(pathname);
};

const isSafeAuthResumeRedirect = (redirect: string): boolean => {
  if (!redirect) {
    return false;
  }

  if (typeof redirect !== 'string' || redirect.length === 0) {
    return false;
  }

  if (!isSafeRedirectUrl(redirect) || isAuthResumeGatePath(redirect)) {
    return false;
  }

  return true;
};

export const persistAuthResumeRedirect = (redirect: string | undefined): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!redirect || !isSafeAuthResumeRedirect(redirect)) {
    return;
  }

  setSharedAuthStorageItem(AUTH_RESUME_REDIRECT_KEY, redirect);
};

export const clearAuthResumeRedirect = (): void => {
  removeSharedAuthStorageItem(AUTH_RESUME_REDIRECT_KEY);
};

export const peekAuthResumeRedirect = (): string | undefined => {
  const value = getSharedAuthStorageItem(AUTH_RESUME_REDIRECT_KEY);

  if (value === null || !isSafeAuthResumeRedirect(value)) {
    return undefined;
  }

  return value;
};

export const consumeAuthResumeRedirect = (): string | undefined => {
  const value = peekAuthResumeRedirect();
  removeSharedAuthStorageItem(AUTH_RESUME_REDIRECT_KEY);

  return value;
};

const isOnEmailVerificationLinkSurface = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return isVerifyRoute(window.location.pathname);
};

/**
 * Email links open `/verify/…` in a new tab with no `redirect` query.
 * Read the shared resume URL without deleting it so every open tab can
 * still continue to the same destination. Cleared on logout.
 *
 * `/verify-user` does not peek storage: that page passes an explicit
 * `redirect` query when one exists, and bare `/verify-user` (e.g. invite
 * join gate) must not resurrect an abandoned skip-onboarding URL.
 */
export const resolvePostAuthRedirect = (redirect: string | undefined, fallback: string): string => {
  if (typeof redirect === 'string' && redirect && isSafeRedirectUrl(redirect)) {
    return redirect;
  }

  if (isOnEmailVerificationLinkSurface()) {
    return peekAuthResumeRedirect() ?? fallback;
  }

  return fallback;
};

/**
 * Shared "gate an unverified user" navigation used by every site that sends a user to
 * `/verify-user`: persist where to resume after verifying, then push there.
 *
 * `redirectTarget` is omitted from the visible `?redirect=` query and the push itself
 * when it is `undefined`, equal to `Routes.home`, or fails `isSafeRedirectUrl` — bare
 * `/verify-user` already falls back to home after verification, and unsafe absolute URLs
 * must not reach the URL bar. Unsafe / home / missing targets also clear any stale
 * resume value in storage.
 */
export type RedirectToVerifyUserRouter = {
  push: (href: string | { pathname: string; query?: Record<string, string> }) => Promise<boolean>;
};

export type RedirectToVerifyUserParams = {
  router: RedirectToVerifyUserRouter;
  redirectTarget: string | undefined;
};

export const redirectToVerifyUser = ({ router, redirectTarget }: RedirectToVerifyUserParams): Promise<boolean> => {
  persistAuthResumeRedirect(redirectTarget);

  const query =
    redirectTarget && redirectTarget !== Routes.home && isSafeRedirectUrl(redirectTarget)
      ? { [QueryParamNames.redirect]: redirectTarget }
      : undefined;

  if (!query) {
    clearAuthResumeRedirect();
  }

  return router.push(query ? { pathname: Routes.verifyUser, query } : Routes.verifyUser);
};
